import React, { useEffect, useState } from "react";
import "./invoice-filter.scss";
import {
  INVOICE_STATUS_OPTIONS,
  invoiceFilterOptions,
} from "../../order-management-helper";
import { camelCase } from "lodash";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import { Checkbox, ListItemText, MenuItem } from "@mui/material";
import OnboardedDateFilter from "../../../../tenant/tenant-list/tenant-filter-options/onboardeddate-filter";
import CompanyFilter from "../../../../leads/lead-logs/leads-log-filters/leads-log-filter-options/company-filter/company-filter";
import SubscriptionPlanFilter from "../../../../tenant/tenant-list/tenant-filter-options/subscription-plan";
import LeadValueFilter from "../../../../leads/lead-logs/leads-log-filters/leads-log-filter-options/lead-value-filter";

interface CompanyOption {
  id: number;
  company_name: string;
}
const InvoiceFilters: React.FC<{
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: Record<string, any>, shouldClose?: boolean) => void;
  filters: Record<string, any>;
  companyNames?: CompanyOption[];
}> = ({ open, onClose, onApplyFilters, filters, companyNames }) => {
  const [filterOptions, setFilterOptions] = useState(() =>
    invoiceFilterOptions.map((opt) => ({ ...opt }))
  );

  const isAnyFilterSelected = filterOptions.some((opt) => opt.value);

  const handleCheckboxChange = (
    label: string,
    values: Record<string, any>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const updated = filterOptions.map((item) => {
      if (item.label === label) {
        const isChecked = !item.value;

        if (!isChecked) {
          if (label === "Invoice Date") {
            setFieldValue("invoiceFromDate", null);
            setFieldValue("invoiceToDate", null);
          } else if (label === "Due Date") {
            setFieldValue("dueFromDate", null);
            setFieldValue("dueToDate", null);
          } else if (label === "Amount") {
            setFieldValue("minAmount", "");
            setFieldValue("maxAmount", "");
          } else if (label === "Transactions") {
            setFieldValue("transactionminValue", "");
            setFieldValue("transactionmaxValue", "");
          } else {
            const key = camelCase(label);
            setFieldValue(key, Array.isArray(values[key]) ? [] : "");
          }
        }

        return { ...item, value: isChecked };
      }
      return item;
    });

    setFilterOptions(updated);
  };

  const getInitialValues = () => {
    const values: Record<string, any> = {};
    filterOptions.forEach((option) => {
      if (option.label === "Invoice Date") {
        values["invoiceFromDate"] = null;
        values["invoiceToDate"] = null;
      } else if (option.label === "Due Date") {
        values["dueFromDate"] = null;
        values["dueToDate"] = null;
      } else if (option.label === "Amount") {
        values["minAmount"] = "";
        values["maxAmount"] = "";
      } else if (option.label === "Transactions") {
        values["transactionminValue"] = "";
        values["transactionmaxValue"] = "";
      } else {
        const key = camelCase(option.label);
        if (option.label === "Company Name") {
          values[key] = [];
        } else {
          values[key] = "";
        }
      }
    });
    return values;
  };
  const [formValues, setFormValues] = useState(getInitialValues());

  const handleApply = (values: Record<string, any>) => {
    const selected = filterOptions
      .filter((opt) => opt.value)
      .reduce(
        (acc, curr) => {
          if (curr.label === "Invoice Date") {
            acc["invoiceFromDate"] = values["invoiceFromDate"];
            acc["invoiceToDate"] = values["invoiceToDate"];
          } else if (curr.label === "Due Date") {
            acc["dueFromDate"] = values["dueFromDate"];
            acc["dueToDate"] = values["dueToDate"];
          } else if (curr.label === "Amount") {
            acc["minAmount"] = values["minAmount"];
            acc["maxAmount"] = values["maxAmount"];
          } else if (curr.label === "Transactions") {
            acc["transactionminValue"] = values["transactionminValue"];
            acc["transactionmaxValue"] = values["transactionmaxValue"];
          } else {
            const key = camelCase(curr.label);
            acc[key] = values[key];
          }
          return acc;
        },
        {} as Record<string, any>
      );
    setFormValues(values);
    onApplyFilters(selected);
  };

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const minDate = dayjs("1900-01-01");

    filterOptions.forEach((option) => {
      if (option.value) {
        if (option.label === "Company Name") {
          if (!values["companyName"] || values["companyName"].length === 0) {
            errors["companyName"] = "Company name cannot be empty";
          }
        } else if (option.label === "Invoice Date") {
          const startDate = values["invoiceFromDate"]
            ? dayjs(values["invoiceFromDate"])
            : null;
          const endDate = values["invoiceToDate"]
            ? dayjs(values["invoiceToDate"])
            : null;

          if (!startDate) {
            errors["invoiceFromDate"] = "Start date is required";
          } else if (startDate.isBefore(minDate)) {
            errors["invoiceFromDate"] = "Invalid date";
          }

          if (!endDate) {
            errors["invoiceToDate"] = "End date is required";
          } else if (endDate.isBefore(minDate)) {
            errors["invoiceToDate"] = "Invalid date";
          }
        } else if (option.label === "Due Date") {
          const startDate = values["dueFromDate"]
            ? dayjs(values["dueFromDate"])
            : null;
          const endDate = values["dueToDate"]
            ? dayjs(values["dueToDate"])
            : null;

          if (!startDate) {
            errors["dueFromDate"] = "Start date is required";
          } else if (startDate.isBefore(minDate)) {
            errors["dueFromDate"] = "Invalid date";
          }

          if (!endDate) {
            errors["dueToDate"] = "End date is required";
          } else if (endDate.isBefore(minDate)) {
            errors["dueToDate"] = "Invalid date";
          }
        } else if (option.label === "Amount") {
          const minValue = Number(values["minAmount"]);
          const maxValue = Number(values["maxAmount"]);
          if (minValue && maxValue && minValue > maxValue) {
            errors["minAmount"] = "Min value should be less than max value";
          }
          if (!minValue) {
            errors["minAmount"] = "Min value is required";
          }
          if (minValue && maxValue && maxValue < minValue) {
            errors["maxAmount"] = "Max value should be greater than min value";
          }
          if (!maxValue) {
            errors["maxAmount"] = "Max value is required";
          }
          if (minValue < 0) {
            errors["minAmount"] = "Min value should be greater than 0";
          }
          if (maxValue < 0) {
            errors["maxAmount"] = "Max value should be greater than 0";
          }
          if (minValue && maxValue && minValue === maxValue) {
            errors["minAmount"] = "Min value should not be equal to max value";
          }
          if (minValue && !maxValue) {
            errors["maxAmount"] = "Max value is required";
          }
          if (maxValue && !minValue) {
            errors["minAmount"] = "Min value is required";
          }
        } else if (option.label === "Transactions") {
          const minValue = Number(values["transactionminValue"]);
          const maxValue = Number(values["transactionmaxValue"]);
          if (minValue && maxValue && minValue > maxValue) {
            errors["transactionminValue"] =
              "Min value should be less than max value";
          }
          if (!minValue) {
            errors["transactionminValue"] = "Min value is required";
          }
          if (minValue && maxValue && maxValue < minValue) {
            errors["transactionmaxValue"] =
              "Max value should be greater than min value";
          }
          if (!maxValue) {
            errors["transactionmaxValue"] = "Max value is required";
          }
          if (minValue < 0) {
            errors["transactionminValue"] =
              "Min value should be greater than 0";
          }
          if (maxValue < 0) {
            errors["transactionmaxValue"] =
              "Max value should be greater than 0";
          }
          if (minValue && maxValue && minValue === maxValue) {
            errors["transactionminValue"] =
              "Min value should not be equal to max value";
          }
          if (minValue && !maxValue) {
            errors["transactionmaxValue"] = "Max value is required";
          }
          if (maxValue && !minValue) {
            errors["transactionminValue"] = "Min value is required";
          }
        } else {
          const key = camelCase(option.label);
          if (
            !values[key] ||
            (Array.isArray(values[key]) && values[key].length === 0)
          ) {
            errors[key] = `${option.label} cannot be empty`;
          }
        }
      }
    });

    return errors;
  };

  const renderFilterField = (
    label: string,
    values: Record<string, any>,
    errors?: Record<string, any>,
    touched?: Record<string, any>,
    setFieldValue?: (field: string, value: any) => void,
    handleBlur?: (e: any) => void
  ) => {
    switch (label) {
      case "Invoice Date":
        return (
          <OnboardedDateFilter
            startDate={values.invoiceFromDate}
            endDate={values.invoiceToDate}
            onDateRangeChange={(start, end) => {
              if (start) setFieldValue?.("invoiceFromDate", start);
              if (end) setFieldValue?.("invoiceToDate", end);
            }}
            startDateError={
              touched?.invoiceFromDate && errors?.invoiceFromDate
                ? String(errors.invoiceFromDate)
                : undefined
            }
            endDateError={
              touched?.invoiceToDate && errors?.invoiceToDate
                ? String(errors.invoiceToDate)
                : undefined
            }
          />
        );
      case "Company Name":
        return (
          <div className="company-filter-wrapper">
            <CompanyFilter
              options={companyNames ?? []}
              value={values.companyName}
              fieldName="companyName"
              onChange={(selected: (number | string)[] | null) =>
                setFieldValue?.("companyName", selected || [])
              }
              handleBlur={() =>
                handleBlur?.({ target: { name: "companyName" } })
              }
              isShowError={touched?.companyName && !!errors?.companyName}
              isOrder={true}
            />
          </div>
        );
      case "Status":
        return (
          <SubscriptionPlanFilter
            value={Array.isArray(values["status"]) ? values["status"] : []}
            plans={INVOICE_STATUS_OPTIONS}
            label="Select Status"
            error={errors?.status}
            onChange={(val: string[]) => setFieldValue?.("status", val)}
            isMultiple={true}
          />
        );
      case "Due Date":
        return (
          <OnboardedDateFilter
            startDate={values.dueFromDate}
            endDate={values.dueToDate}
            onDateRangeChange={(start, end) => {
              if (start) setFieldValue?.("dueFromDate", start);
              if (end) setFieldValue?.("dueToDate", end);
            }}
            startDateError={
              touched?.dueFromDate && errors?.dueFromDate
                ? String(errors.dueFromDate)
                : undefined
            }
            endDateError={
              touched?.dueToDate && errors?.dueToDate
                ? String(errors.dueToDate)
                : undefined
            }
          />
        );
      case "Amount":
        return (
          <LeadValueFilter
            minValue={values.minAmount}
            maxValue={values.maxAmount}
            onValueChange={(e: any, type: "min" | "max") => {
              if (type === "min") {
                setFieldValue?.("minAmount", e.target.value);
              } else {
                setFieldValue?.("maxAmount", e.target.value);
              }
            }}
            minError={touched?.minAmount && errors?.minAmount}
            maxError={touched?.maxAmount && errors?.maxAmount}
          />
        );
      case "Transactions":
        return (
          <LeadValueFilter
            minValue={values.transactionminValue}
            maxValue={values.transactionmaxValue}
            onValueChange={(e: any, type: "min" | "max") => {
              if (type === "min") {
                setFieldValue?.("transactionminValue", e.target.value);
              } else {
                setFieldValue?.("transactionmaxValue", e.target.value);
              }
            }}
            minError={
              touched?.transactionminValue && errors?.transactionminValue
            }
            maxError={
              touched?.transactionmaxValue && errors?.transactionmaxValue
            }
          />
        );
      default:
        return null;
    }
    return null;
  };

  useEffect(() => {
    const updatedOptions = invoiceFilterOptions.map((opt) => {
      const key =
        opt.label === "Invoice Date"
          ? "invoiceFromDate" in filters || "invoiceToDate" in filters
          : opt.label === "Due Date"
            ? "dueFromDate" in filters || "dueToDate" in filters
            : opt.label === "Amount"
              ? "minAmount" in filters || "maxAmount" in filters
              : opt.label === "Transactions"
                ? "transactionminValue" in filters ||
                  "transactionmaxValue" in filters
                : camelCase(opt.label) in filters;

      return { ...opt, value: !!key };
    });

    setFilterOptions(updatedOptions);

    setFormValues((prev) => ({
      ...getInitialValues(),
      ...filters,
    }));
  }, [filters]);

  return (
    <div className={`order-filter-drawer ${open ? "open" : ""}`}>
      <div className="filter-header">
        <div className="drawer-header">
          <h4>Filters</h4>
          <CloseIcon
            fontSize="small"
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </div>
        <h4>Filter By Fields</h4>
      </div>
      <Formik
        initialValues={formValues}
        enableReinitialize
        validate={validate}
        onSubmit={handleApply}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          resetForm,
          handleBlur,
        }) => {
          return (
            <Form className="drawer-content" onSubmit={handleSubmit}>
              {filterOptions.map((option) => (
                <div key={option.label} className="filter-option-row">
                  <MenuItem
                    key={option.label}
                    value={option.label}
                    className="filter-menu-item"
                    onClick={() =>
                      handleCheckboxChange(option.label, values, setFieldValue)
                    }
                  >
                    <Checkbox checked={option.value} />
                    <ListItemText primary={option.label} />
                  </MenuItem>

                  {option.value &&
                    renderFilterField(
                      option.label,
                      values,
                      errors,
                      touched,
                      setFieldValue,
                      handleBlur
                    )}
                </div>
              ))}

              {isAnyFilterSelected && (
                <div className="lead-filter-buttons">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      resetForm();
                      setFormValues(getInitialValues());
                      setFilterOptions(
                        invoiceFilterOptions.map((opt) => ({ ...opt }))
                      );
                      onApplyFilters({}, false);
                    }}
                  >
                    Reset
                  </button>
                  <button type="submit" className="next-button-wrapper">
                    Apply
                  </button>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
export default InvoiceFilters;
